import { useState, useEffect } from 'react';
declare global {
  interface Navigator {
    standalone?: boolean;
  }
}

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

// Service Worker registration functions for conditional PWA
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });
      console.log('Service Worker registered successfully');
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
}

export async function unregisterServiceWorker(): Promise<boolean> {
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
      console.log('Service Worker unregistered successfully');
      return true;
    } catch (error) {
      console.error('Service Worker unregistration failed:', error);
      return false;
    }
  }
  return false;
}
export const useIsAppInstalled = (): boolean => {
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const isIOSInstalled = 
      window.navigator.standalone || 
      window.matchMedia('(display-mode: standalone)').matches;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    setIsInstalled(isIOSInstalled || isStandalone);
  }, []);

  return isInstalled;
};
export const useInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [userChoice, setUserChoice] = useState<string | null>(null);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setUserChoice('accepted');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installPrompt) return false;
    installPrompt.prompt();
    const choiceResult = await installPrompt.userChoice;
    
    setUserChoice(choiceResult.outcome);
    setInstallPrompt(null);
    
    return choiceResult.outcome === 'accepted';
  };
  const getPlatform = () => {
    const ua = navigator.userAgent;
    
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/Windows/.test(ua)) return 'windows';
    if (/Mac OS/.test(ua)) return 'macos';
    if (/Linux/.test(ua)) return 'linux';
    
    return 'unknown';
  };

  const getBrowser = () => {
    const ua = navigator.userAgent;
    
    if (/chrome|chromium|crios/i.test(ua)) return 'chrome';
    if (/firefox|fxios/i.test(ua)) return 'firefox';
    if (/safari/i.test(ua) && !/chrome|chromium|crios/i.test(ua)) return 'safari';
    if (/MSIE|Trident/i.test(ua)) return 'ie';
    if (/edge/i.test(ua)) return 'edge';
    if (/opera|opr/i.test(ua)) return 'opera';
    
    return 'unknown';
  };

  return { 
    installPrompt, 
    triggerInstall, 
    userChoice, 
    isInstallable: !!installPrompt,
    platform: getPlatform(),
    browser: getBrowser()
  };
};
export const getInstallInstructions = (platform: string, browser: string): string => {
  if (platform === 'ios' && browser === 'safari') {
    return 'Tap the share button and then "Add to Home Screen"';
  }
  
  if (platform === 'android' && browser === 'chrome') {
    return 'Tap the menu button and then "Add to Home Screen"';
  }
  
  if (platform === 'android' && browser === 'firefox') {
    return 'Tap the menu button and then "Install"';
  }
  
  if ((platform === 'windows' || platform === 'macos' || platform === 'linux') && browser === 'chrome') {
    return 'Click the install icon in the address bar';
  }
  
  if ((platform === 'windows' || platform === 'macos' || platform === 'linux') && browser === 'edge') {
    return 'Click the install icon in the address bar';
  }
  
  return 'Use the browser menu to install this app';
}; 